import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import JoditEditor from 'jodit-react';
import { get, post } from '../../ApiServices/ApiServices';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

const EmailEditor = ({ data, state, onEmailSent }) => {
    const [isSending, setIsSending] = useState(false);
    const [noticeTemplates, setNoticeTemplates] = useState([]);
    const [editorContent, setEditorContent] = useState('');
    const navigate = useNavigate();
    const editor = useRef(null);
    const ref1 = useRef();

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors, isSubmitting, isValid }
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            noticeType: '',
            subject: '',
            content: ''
        }
    });

    const watchNoticeType = watch('noticeType');

    const processTemplate = (template) => {
        let processedTemplate = template;

        // Replace ticket number
        processedTemplate = processedTemplate.replace(/#A010/g, `#${data?.ticket || ''}`);

        // Replace customer name
        processedTemplate = processedTemplate.replace(/\[Customer's Name\]/g,
            `${data?.customer?.firstName || ''} ${data?.customer?.lastName || ''}`.trim() || 'Valued Customer');

        // Replace user first name
        processedTemplate = processedTemplate.replace(/\[Your First Name\]/g,
            data?.user?.firstName || 'Team Member');

        // Replace user last name
        processedTemplate = processedTemplate.replace(/\[Your Last Name\]/g,
            data?.user?.lastName || '');

        // Replace company name
        processedTemplate = processedTemplate.replace(/\[Company Name\]/g,
            data?.customer?.companyName || '');

        // Replace account number
        processedTemplate = processedTemplate.replace(/\[Account Number\]/g,
            data?.customer?.account || '');

        // Replace event date
        if (data?.event_date || data?.wedding_date) {
            const eventDate = new Date(data?.event_date || data?.wedding_date);
            processedTemplate = processedTemplate.replace(/\[Event Date\]/g,
                eventDate.toLocaleDateString() || '');
        }

        return processedTemplate;
    };

    // ✅ Memoized config to prevent JoditEditor from re-initializing on every render
    const config = useMemo(() => ({
        readonly: false,
        toolbar: true,
        spellcheck: true,
        language: 'en',
        toolbarButtonSize: 'medium',
        toolbarAdaptive: false,
        showCharsCounter: false,
        showWordsCounter: false,
        showXPathInStatusbar: false,
        statusbar: false,
        askBeforePasteHTML: true,
        askBeforePasteFromWord: true,
        defaultActionOnPaste: 'insert_clear_html',
        buttons: 'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,|,image,link,|,align,|,undo,redo',
        uploader: {
            insertImageAsBase64URI: true
        },
        height: 400
    }), []);

    // Get templates on mount
    useEffect(() => {
        const getTemplate = async () => {
            try {
                const api = `notice-templates`;
                const response = await get(api);
                if (response?.success === true) {
                    setNoticeTemplates(response?.data);
                }
            } catch (err) {
                if (err?.message === "Invalid or expired token" || err?.message === "Invalid Token") {
                    localStorage.removeItem('token');
                    navigate('/login');
                }
                console.error("Error fetching notice templates:", err);
            }
        };
        getTemplate();
    }, [navigate]);

    // ✅ Single useEffect (removed duplicate) — update subject and body when notice type changes
    useEffect(() => {
        if (watchNoticeType) {
            const template = noticeTemplates.find(t => t.noticeType === watchNoticeType);
            if (template) {
                setValue('subject', template.subject);
                const processedContent = processTemplate(template.body);
                setEditorContent(processedContent);
                setValue('content', processedContent, { shouldValidate: true });
            }
        } else {
            setValue('subject', '');
            setEditorContent('');
            setValue('content', '');
        }
    }, [watchNoticeType, noticeTemplates, setValue]);

    // ✅ Keep editorContent state and form value in sync when user edits
    const handleEditorChange = useCallback((newContent) => {
        setEditorContent(newContent);
        setValue('content', newContent, { shouldValidate: true });
    }, [setValue]);

    const onSubmit = async (formData) => {
        setIsSending(true);
        try {
            const endpoint = `send-notice-email`;
            const payload = {
                delinquentOrderId: state,
                subject: formData.subject,
                body: editorContent,        // ✅ Use editorContent state directly (not formData.content)
                noticeType: formData.noticeType
            };
            const response = await post(endpoint, payload);

            if (response.success == true) {
                toast.success(response?.message, {
                    position: "top-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    closeButton: false,
                });
                reset();
                setEditorContent('');
                ref1.current.click();

                if (onEmailSent) {
                    await onEmailSent();
                }
            }
        } catch (error) {
            toast.error(error?.message, {
                position: "top-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                closeButton: false,
            });
            reset();
            setEditorContent('');
            ref1.current.click();
            console.error("Error sending email:", error);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <>
            <div className="modal fade" tabIndex="-1" id="emailNewTemplate">
                <div className="modal-dialog modal-lg modalWidth820" role="document">
                    <div className="modal-content" style={{ width: "100%" }}>
                        <div className="modal-header">
                            <h2>Send Email</h2>
                            <button className="close" data-bs-dismiss="modal" ref={ref1}>
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)} className="email-form">
                            <div className="row mb-3 mt-2">
                                <div className="col-sm-12 d-flex align-items-center gap-2 fs-13">
                                    <p className="mb-2"><b>To:</b> <span>{data?.customer?.email}</span></p>
                                    <p className="mb-2"><b>Acc#:</b> <span>{data?.customer?.account}</span></p>
                                    <p className="mb-2"><b>Acc name:</b> <span>{data?.customer?.companyName}</span></p>
                                    <p className="mb-2"><b>Owner name:</b> <span>{data?.customer?.firstName} {data?.customer?.lastName}</span></p>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="form-group inputCardBox">
                                        <label htmlFor="noticeType">Notice Type: <span className="redC">*</span></label>
                                        <select
                                            id="noticeType"
                                            style={{ appearance: "auto" }}
                                            {...register('noticeType', { required: 'Notice type is required' })}
                                            className={errors.noticeType ? 'error' : ''}
                                        >
                                            <option value="">Select Notice Type</option>
                                            {noticeTemplates.map((notice) => (
                                                <option key={notice.id} value={notice.noticeType}>
                                                    {notice.noticeType}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.noticeType && (
                                            <span className="error-message">{errors.noticeType.message}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="form-group inputCardBox">
                                        <label htmlFor="subject">Subject: <span className="redC">*</span></label>
                                        <input
                                            type="text"
                                            id="subject"
                                            {...register('subject', {
                                                required: 'Subject is required',
                                                validate: value => value.trim() !== '' || 'Subject cannot be empty'
                                            })}
                                            className={errors.subject ? 'error' : ''}
                                            placeholder="Email subject"
                                        />
                                        {errors.subject && (
                                            <span className="error-message">{errors.subject.message}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-12">
                                    <div className="form-group editor-container">
                                        <label>Email Content: <span className="redC">*</span></label>
                                        <JoditEditor
                                            ref={editor}
                                            value={editorContent}
                                            config={config}
                                            onChange={handleEditorChange}
                                            tabIndex={1}
                                        />
                                        {errors.content && (
                                            <span className="error-message">{errors.content.message}</span>
                                        )}
                                        <input
                                            type="hidden"
                                            {...register('content', {
                                                required: 'Email content is required',
                                                validate: value => value.trim() !== '' || 'Content cannot be empty'
                                            })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="submit"
                                    className="send-button"
                                    style={isSubmitting || !isValid ? { pointerEvents: "none" } : {}}
                                >
                                    {isSubmitting ? <>
                                        <span className="spinner"></span> Sending...
                                    </> : 'Send Email'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EmailEditor;